'use client';

import ThreeColCardBar from '@/components/_rebrand/animations/ThreeColCardBar';
import SectionWrapper from '@/components/_rebrand/craft/SectionWrapper';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import { StyleOverride } from '@/types/craft-style-overrides';
import cn from 'classnames';
import { FC } from 'react';

export type ThreeColumnDemoProps = {
  title: React.ReactNode,
  features: {
    icon: React.ReactNode,
    title: string;
    description: string | JSX.Element;
  }[]
  styleOverrides?: StyleOverride;
  /** Optional link CTA replacing the default "Request a demo" modal button. */
  cta?: {
    text: string;
    href: string;
  };
}

/**
 * ThreeColumnDemo, called "Why Front" in Craft, displays 3 cards with a simple animated element.
 * - Note that the cards have a white background, so the component should have a colored background from a parent element.
 * 
 * @param title - The title of the three column demo - uses <SerifAndSansSerifHeadline /> component in Craft.
 * @param features - The features of the three column demo  
 * @param styleOverrides - The style overrides of the three column demo.
 * @param cta - Optional link CTA; when provided it replaces the default "Request a demo" modal button.
 * @returns A three column demo component.
 */

const ThreeColumnDemo: FC<ThreeColumnDemoProps> = ({
  title,
  features = [],
  styleOverrides,
  cta
}) => {

  const { modalActionHandler } = useModalButtonActions();

  return (
    <div className="text-white">
      <SectionWrapper styleOverrides={styleOverrides} className="-mt-header-height-mobile md:-mt-header-height-desktop">
        <div className={cn(!styleOverrides ? 'pt-header-height-mobile md:pt-header-height-desktop' : 'py-1')}>
          <div className={cn('pt-0', !styleOverrides ? 'md:pt-12' : 'py-0', 'px-container-mobile md:px-container-desktop mx-auto max-w-grid')}>
            <div className="grid gap-2 md:gap-3 text-left">
              <div className="grid gap-3 md:gap-3 text-left">
                <div className="flex justify-center">
                  <h1
                    className="text-blue-900 text-25/33 md:text-36/42 font-500 max-w-[900px] text-left md:text-center">
                    {title}
                  </h1>
                </div>
                <div className="flex justify-center">
                  {cta ? (
                    <a
                      className={cn('block button button-size-medium button-primary-citron mr-[16px]')}
                      href={cta.href}
                    >
                      {cta.text}
                    </a>
                  ) : (
                    <button
                      className={cn('block button button-size-medium button-primary-citron mr-[16px]')}
                      onClick={() => {
                        modalActionHandler('demo');
                      }}
                    >
                      Request a demo
                    </button>
                  )}
                </div>
                <ThreeColCardBar interval={2000} />
              </div>
              <div className="flex justify-between flex-wrap md:flex-nowrap mb-4">
                {features.map((item, index) => {
                  return (
                    <div
                      key={`feature-card-${index}`}
                      className="flex flex-wrap flex-col w-full mb-[32px] mr-[16px] md:mb-0 bg-white rounded-[14px] p-3"
                    >
                      <div className="flex flex-col gap-3">
                        <div className="flex" id="icon">
                          <span
                            className="flex w-[32px] h-[32px] bg-citron-500 rounded-[50%] justify-center items-center mr-[16px]">
                            {item.icon}
                          </span>
                        </div>
                        <div className="flex text-25/30 text-blue-900 font-500" id="textHeader">
                          {item.title}
                        </div>
                      </div>
                      <div className="flex text-17/27 text-gray-700 mt-[18px]">{item.description}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
};

export default ThreeColumnDemo;
